import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Fingerprint, KeyRound, ShieldCheck, Trash2 } from "lucide-react";
import { unscopedKey } from "@/lib/queryClient";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/Button";
import type { KratosFlow } from "@/lib/kratos";
import {
  createFlow,
  firstFlowError,
  KratosError,
  KratosSessionRefreshError,
  kratosErrorCopy,
  submitFlow,
} from "@/lib/kratos";
import { KratosPasswordForm } from "@/features/auth/kratos/KratosPasswordForm";
import {
  flowSupportsPasskeyRegistration,
  passkeyRegister,
  webauthnSupported,
} from "@/features/auth/kratos/webauthn";

/**
 * Account security management against the Kratos settings flow:
 * change password, add/remove passkeys. Settings flows demand a
 * recently-authenticated Kratos session (`privileged_session_max_age`)
 * — on Kratos's refresh error this panel prompts an inline password
 * re-auth instead of dead-ending.
 *
 * Passkey rename is intentionally absent: Kratos models display names
 * at creation only — remove + re-add is the supported path.
 */
export function KratosSettingsPanel() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [flow, setFlow] = useState<KratosFlow | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [noKratosSession, setNoKratosSession] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [confirmTotpUnlink, setConfirmTotpUnlink] = useState(false);

  const loadFlow = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setFlow(await createFlow("settings"));
      setNeedsReauth(false);
      setNoKratosSession(false);
    } catch (err) {
      if (err instanceof KratosSessionRefreshError) {
        setNeedsReauth(true);
      } else if (err instanceof KratosError && err.status === 401) {
        // No live Kratos session at all (e.g. the app session outlived
        // it). A fresh sign-in restores both.
        setNoKratosSession(true);
      } else {
        setError(kratosErrorCopy(err));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFlow();
  }, [loadFlow]);

  const refreshSecuritySummary = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: unscopedKey("me-security") });
  }, [queryClient]);

  const submitSettings = useCallback(
    async (values: Record<string, unknown>, successMessage: string) => {
      if (!flow || busy) return;
      setBusy(true);
      setError(null);
      try {
        const outcome = await submitFlow("settings", flow, values);
        if (outcome.kind === "flow") {
          setFlow(outcome.flow);
          if (outcome.flow.state === "success") {
            toast.success(successMessage);
            refreshSecuritySummary();
          } else {
            const message = firstFlowError(outcome.flow);
            if (message) setError(message);
          }
        }
      } catch (err) {
        if (err instanceof KratosSessionRefreshError) {
          setNeedsReauth(true);
        } else {
          setError(kratosErrorCopy(err));
        }
      } finally {
        setBusy(false);
      }
    },
    [busy, flow, refreshSecuritySummary, toast],
  );

  const passkeys = useMemo(() => {
    if (!flow) return [];
    return flow.ui.nodes
      .filter(
        (node) =>
          node.group === "passkey" && node.attributes.name === "passkey_remove",
      )
      .map((node) => ({
        id: String(node.attributes.value ?? ""),
        label: node.meta?.label?.text ?? "Passkey",
      }));
  }, [flow]);

  // TOTP (#358) — Kratos's settings flow carries either the
  // enrollment trio (QR img node + secret text node + `totp_code`
  // input) or, once enrolled, the `totp_unlink` submit.
  const totp = useMemo(() => {
    if (!flow) return { enrolled: false, qr: null as string | null, secret: null as string | null };
    const nodes = flow.ui.nodes.filter((node) => node.group === "totp");
    const enrolled = nodes.some(
      (node) => node.attributes.name === "totp_unlink",
    );
    const qrNode = nodes.find((node) => node.type === "img");
    const secretNode = nodes.find(
      (node) => node.attributes.id === "totp_secret_key",
    );
    const secretText = secretNode?.attributes.text as
      | { text?: string }
      | undefined;
    return {
      enrolled,
      qr: qrNode ? String(qrNode.attributes.src ?? "") : null,
      secret: secretText?.text ?? null,
    };
  }, [flow]);

  if (loading) {
    return (
      <p className="rounded border border-rule bg-elevated px-3 py-2 text-sm text-ink-muted">
        Loading security settings...
      </p>
    );
  }

  if (needsReauth || noKratosSession) {
    return (
      <KratosReauthPrompt
        reason={
          needsReauth
            ? "Confirm your password to manage passkeys and password settings."
            : "Your secure session ended. Confirm your password to manage security settings."
        }
        onAuthenticated={loadFlow}
      />
    );
  }

  if (!flow) {
    return (
      <p
        data-testid="kratos-settings-error"
        className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
      >
        {error ?? "Security settings are temporarily unavailable."}
      </p>
    );
  }

  const handleAddPasskey = async () => {
    if (!flow || busy) return;
    setBusy(true);
    setError(null);
    try {
      const attestation = await passkeyRegister(flow);
      setBusy(false);
      // Settings flows submit `passkey_settings_register` — unlike
      // registration flows (`passkey_register`). Kratos's passkey
      // strategy has no display-name field; it derives the label from
      // the identity's `passkey.display_name` trait (the email).
      await submitSettings(
        {
          method: "passkey",
          passkey_settings_register: attestation,
        },
        "Passkey added",
      );
    } catch {
      // User dismissed the platform passkey dialog.
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="kratos-settings-panel">
      <div className="rounded border border-rule bg-elevated p-3">
        <p className="font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
          Passkeys
        </p>
        <p className="mt-2 text-sm leading-snug text-ink">
          Passkeys sign you in with your device&rsquo;s screen lock — no
          password to type or phish.
        </p>
        {passkeys.length ? (
          <ul className="mt-3 flex flex-col gap-2" data-testid="kratos-passkey-list">
            {passkeys.map((passkey) => (
              <li
                key={passkey.id}
                className="flex items-center justify-between gap-3 rounded border border-rule bg-surface px-3 py-2"
              >
                <span className="flex min-w-0 items-center gap-2 text-sm text-ink">
                  <Fingerprint size={15} strokeWidth={2.2} aria-hidden="true" />
                  <span className="truncate">{passkey.label}</span>
                </span>
                {confirmRemoveId === passkey.id ? (
                  <span className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmRemoveId(null)}
                      className="rounded px-1 py-0.5 font-mono text-[0.65rem] text-ink-muted hover:text-ink hover:underline"
                    >
                      Keep
                    </button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() =>
                        void submitSettings(
                          { method: "passkey", passkey_remove: passkey.id },
                          "Passkey removed",
                        ).then(() => setConfirmRemoveId(null))
                      }
                      data-testid="kratos-passkey-remove-confirm"
                    >
                      Remove
                    </Button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmRemoveId(passkey.id)}
                    aria-label={`Remove ${passkey.label}`}
                    data-testid="kratos-passkey-remove"
                    className="shrink-0 rounded p-1 text-ink-muted transition-colors hover:bg-field hover:text-destructive"
                  >
                    <Trash2 size={15} strokeWidth={2.2} aria-hidden="true" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 font-mono text-xs text-ink-muted" data-testid="kratos-passkey-empty">
            No passkeys yet.
          </p>
        )}
        {flowSupportsPasskeyRegistration(flow) && webauthnSupported() ? (
          <div className="mt-3 flex justify-end border-t border-rule pt-3">
            <Button
              type="button"
              size="sm"
              onClick={() => void handleAddPasskey()}
              disabled={busy}
              data-testid="kratos-passkey-add"
            >
              <Fingerprint size={15} strokeWidth={2.2} />
              Add passkey
            </Button>
          </div>
        ) : null}
      </div>

      <div className="rounded border border-rule bg-elevated p-3" data-testid="kratos-totp-section">
        <p className="font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
          Authenticator app
        </p>
        {totp.enrolled ? (
          <div className="mt-2 flex items-start justify-between gap-3">
            <p className="text-sm leading-snug text-ink" data-testid="kratos-totp-enrolled">
              Time-based codes are enabled. Privileged actions can step
              up to a second factor with your authenticator app.
            </p>
            {confirmTotpUnlink ? (
              <span className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmTotpUnlink(false)}
                  className="rounded px-1 py-0.5 font-mono text-[0.65rem] text-ink-muted hover:text-ink hover:underline"
                >
                  Keep
                </button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() =>
                    void submitSettings(
                      { method: "totp", totp_unlink: true },
                      "Authenticator app removed",
                    ).then(() => setConfirmTotpUnlink(false))
                  }
                  data-testid="kratos-totp-unlink-confirm"
                >
                  Remove
                </Button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmTotpUnlink(true)}
                aria-label="Remove authenticator app"
                data-testid="kratos-totp-unlink"
                className="shrink-0 rounded p-1 text-ink-muted transition-colors hover:bg-field hover:text-destructive"
              >
                <Trash2 size={15} strokeWidth={2.2} aria-hidden="true" />
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="mt-2 text-sm leading-snug text-ink">
              Scan the QR code with an authenticator app (1Password,
              Google Authenticator, ...), then enter a 6-digit code to
              enable time-based one-time codes.
            </p>
            {totp.qr ? (
              <div className="mt-3 flex flex-wrap items-start gap-4">
                <img
                  src={totp.qr}
                  alt="Authenticator enrollment QR code"
                  width={144}
                  height={144}
                  data-testid="kratos-totp-qr"
                  className="rounded border border-rule bg-paper p-1"
                />
                {totp.secret ? (
                  <div className="min-w-0">
                    <p className="font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
                      Manual entry key
                    </p>
                    <p
                      data-testid="kratos-totp-secret"
                      className="mt-1 break-all font-mono text-xs text-ink"
                    >
                      {totp.secret}
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}
            <form
              className="mt-3 flex items-end gap-2 border-t border-rule pt-3"
              onSubmit={(e) => {
                e.preventDefault();
                void submitSettings(
                  { method: "totp", totp_code: totpCode.trim() },
                  "Authenticator app enabled",
                ).then(() => setTotpCode(""));
              }}
            >
              <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs">
                <span className="font-display uppercase tracking-wider text-ink-muted">
                  Verification code
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  data-testid="kratos-totp-code"
                  className="rounded border border-rule bg-canvas px-3 py-2 font-mono text-sm tracking-[0.25em] text-ink focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </label>
              <Button
                type="submit"
                size="sm"
                disabled={busy || !totpCode.trim()}
                data-testid="kratos-totp-enable"
              >
                <ShieldCheck size={15} strokeWidth={2.2} />
                Enable
              </Button>
            </form>
          </>
        )}
      </div>

      <div className="rounded border border-rule bg-elevated p-3">
        <p className="font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
          Password
        </p>
        <p className="mt-2 text-sm leading-snug text-ink">
          Change the password used for email sign-in.
        </p>
        <div className="mt-3 border-t border-rule pt-3">
          <KratosPasswordForm
            initialFlow={flow}
            submitLabel="Change password"
            onSaved={() => {
              toast.success("Password updated");
              refreshSecuritySummary();
              void loadFlow();
            }}
            onSessionRefreshRequired={() => setNeedsReauth(true)}
            testIdPrefix="kratos-settings-password"
          />
        </div>
      </div>

      {error ? (
        <p
          data-testid="kratos-settings-error"
          className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Inline Kratos re-auth: a `refresh=true` login flow scoped to this
 * panel, so privileged settings never bounce the user out of the page. */
function KratosReauthPrompt({
  reason,
  onAuthenticated,
}: {
  reason: string;
  onAuthenticated: () => void | Promise<void>;
}) {
  const [flow, setFlow] = useState<KratosFlow | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setFlow(await createFlow("login", { refresh: "true" }));
      } catch (err) {
        setError(kratosErrorCopy(err));
      }
    })();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flow || busy) return;
    setBusy(true);
    setError(null);
    try {
      const outcome = await submitFlow("login", flow, {
        method: "password",
        identifier: email.trim(),
        password,
      });
      if (outcome.kind === "success") {
        await onAuthenticated();
        return;
      }
      setFlow(outcome.flow);
      setError(firstFlowError(outcome.flow));
    } catch (err) {
      setError(kratosErrorCopy(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      data-testid="kratos-reauth-form"
      className="flex flex-col gap-3 rounded border border-rule bg-elevated p-3"
      onSubmit={handleSubmit}
    >
      <p className="flex items-center gap-2 text-sm text-ink">
        <KeyRound size={15} strokeWidth={2.2} aria-hidden="true" />
        {reason}
      </p>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Email
        </span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
          data-testid="kratos-reauth-email"
          className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Password
        </span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
          data-testid="kratos-reauth-password"
          className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </label>
      {error ? (
        <p className="font-mono text-xs text-destructive" data-testid="kratos-reauth-error">
          {error}
        </p>
      ) : null}
      <Button type="submit" size="sm" disabled={busy} data-testid="kratos-reauth-submit" className="self-end">
        {busy ? "Confirming..." : "Confirm"}
      </Button>
    </form>
  );
}
